import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { auth } from "../lib/firebase";
import { useAuthState } from "react-firebase-hooks/auth";
import { Zap, WifiOff } from "lucide-react";
import {
  aggregateByCrop,
  getCachedProducts,
  subscribeProducts,
} from "../services/farmPulseService";
import { errorDev } from "../utils/logger.js";

const integerFmt = new Intl.NumberFormat("en-US", {
  maximumFractionDigits: 0,
});
const signedFmt = new Intl.NumberFormat("en-US", {
  maximumFractionDigits: 1,
  signDisplay: "always",
});

function compactCropCode(crop = "") {
  const parts = String(crop).split(" ").filter(Boolean);
  if (parts.length === 0) return "--";
  if (parts.length === 1) return parts[0].slice(0, 3).toUpperCase();
  return `${parts[0][0] || ""}${parts[1][0] || ""}${parts[2]?.[0] || ""}`.toUpperCase();
}

function useMarketData() {
  const [products, setProducts] = useState(() => getCachedProducts());
  const [loading, setLoading] = useState(products.length === 0);
  const [error, setError] = useState(null);

  useEffect(() => {
    const unsub = subscribeProducts(
      (rows) => {
        setProducts(Array.isArray(rows) ? rows : []);
        setLoading(false);
        setError(null);
      },
      (err) => {
        errorDev("Product subscription error:", err);
        setError(err?.message || "Connection error");
        setLoading(false);
      }
    );

    return () => {
      try {
        unsub?.();
      } catch (err) {
        errorDev("Operation failed:", err);
      }
    };
  }, []);

  return { products, loading, error };
}

function MarketCard({ row }) {
  const hasPrice = Number.isFinite(Number(row?.last));
  const hasMovement = Number.isFinite(Number(row?.change30));
  const unit = row?.priceUnit || row?.unit || "kg";

  let movementLabel, movementColor, movementIcon;

  if (hasMovement) {
    const pct = Number(row.change30);
    const sign = signedFmt.format(pct);
    if (pct > 0) {
      movementLabel = `${sign}%`;
      movementColor = "text-green-400";
      movementIcon = "▲";
    } else if (pct < 0) {
      movementLabel = `${sign}%`;
      movementColor = "text-red-400";
      movementIcon = "▼";
    } else {
      movementLabel = "Stable";
      movementColor = "text-emerald-50/70";
      movementIcon = "";
    }
  } else if (hasPrice) {
    movementLabel = "Stable";
    movementColor = "text-emerald-50/70";
    movementIcon = "";
  } else {
    movementLabel = "Unavailable";
    movementColor = "text-emerald-50/70";
    movementIcon = "";
  }

  const initials = row?.crop
    ? row.crop
        .split(" ")
        .map((w) => w[0])
        .join("")
        .slice(0, 2)
        .toUpperCase()
    : "?";

  return (
    <div className="bg-green-950/30 border border-emerald-700/40 rounded-2xl p-5 hover:border-emerald-700/60 transition-colors duration-200">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3 min-w-0">
          <div className="w-10 h-10 rounded-full bg-emerald-400/15 flex items-center justify-center text-emerald-300 font-semibold text-xs shrink-0">
            {initials}
          </div>
          <div className="min-w-0">
            <span className="text-[10px] font-semibold text-emerald-50/60 uppercase tracking-wider block">
              {compactCropCode(row?.crop)}
            </span>
            <span className="text-sm font-medium text-emerald-100 truncate block">
              {row?.crop || "Unknown"}
            </span>
          </div>
        </div>
        {movementIcon && (
          <span className={`text-xs font-semibold ${movementColor}`}>
            {movementIcon}
          </span>
        )}
      </div>

      <div className="mb-3">
        {hasPrice ? (
          <span className="text-lg font-semibold text-white">
            {integerFmt.format(Number(row.last))}
          </span>
        ) : (
          <span className="text-sm text-emerald-50/70">Unavailable</span>
        )}
        {hasPrice && (
          <span className="text-sm text-emerald-50/70"> RWF / {unit}</span>
        )}
      </div>

      <div className="flex items-center justify-between pt-3 border-t border-emerald-700/30">
        <span className={`text-xs font-semibold ${movementColor}`}>
          {movementLabel}
        </span>
        {row?.vol30 && (
          <span className="text-xs text-emerald-50/50">
            {row.vol30} report{row.vol30 > 1 ? "s" : ""}
          </span>
        )}
      </div>
    </div>
  );
}

function CardSkeleton() {
  return (
    <div className="bg-green-950/30 border border-emerald-700/40 rounded-2xl p-5 animate-pulse">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-emerald-900/50" />
          <div>
            <div className="h-3 w-8 bg-emerald-900/50 rounded mb-1" />
            <div className="h-4 w-20 bg-emerald-900/50 rounded" />
          </div>
        </div>
        <div className="h-3 w-3 bg-emerald-900/50 rounded" />
      </div>
      <div className="h-5 w-24 bg-emerald-900/50 rounded mb-1" />
      <div className="h-3 w-16 bg-emerald-900/50 rounded mb-3" />
      <div className="h-3 w-full bg-emerald-900/50 rounded" />
    </div>
  );
}

export default function OrrithLanding() {
  const navigate = useNavigate();
  const [user] = useAuthState(auth);
  const { products, loading, error } = useMarketData();

  const marketRows = useMemo(() => {
    if (!products.length) return [];
    const rows = aggregateByCrop(products, "ALL");
    return rows
      .filter((r) => Number.isFinite(Number(r?.last)))
      .sort((a, b) => {
        const diff = (b.vol30 ?? 0) - (a.vol30 ?? 0);
        if (diff !== 0) return diff;
        return (b.volume ?? 0) - (a.volume ?? 0);
      })
      .slice(0, 6);
  }, [products]);

  const handleContributeData = () => {
    if (user) navigate("/farmpulse?tab=contribute");
    else navigate("/signup?redirect=/farmpulse?tab=contribute");
  };

  const handleStartPilot = () => {
    if (user) navigate("/careers");
    else navigate("/signup");
  };

  return (
    <div className="min-h-screen bg-green-900 text-white antialiased overflow-x-hidden">
      <main className="relative pt-16 pb-20">
        <div className="max-w-7xl mx-auto px-8 space-y-10 lg:space-y-14">
          {/* ─── Market Intelligence Section ─── */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-10">
            {/* Left: Editorial block (equal height, vertically centered) */}
            <div className="flex flex-col justify-center">
              <h2 className="text-3xl lg:text-4xl font-black mb-4 text-emerald-100">
                Build Your Agricultural Insights
              </h2>
              <p className="text-lg text-emerald-50/80 mb-6 max-w-md leading-relaxed">
                Track real-time agricultural price trends, crop intelligence,
                and market reports across regions.
              </p>
              <button
                onClick={handleContributeData}
                className="w-fit px-8 py-3 bg-white text-green-950 font-semibold text-base rounded-full hover:bg-neutral-100 transition-colors focus:outline-none focus:ring-2 focus:ring-emerald-200"
                aria-label="Contribute data to FarmPulse"
              >
                Contribute
              </button>
            </div>

            {/* Right: 6 crop cards */}
            <div className="flex flex-col gap-4">
              {loading && marketRows.length === 0 ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {Array.from({ length: 6 }).map((_, i) => (
                    <CardSkeleton key={i} />
                  ))}
                </div>
              ) : error && marketRows.length === 0 ? (
                <div className="bg-green-950/30 border border-emerald-700/40 rounded-2xl p-6 text-center">
                  <WifiOff className="w-8 h-8 text-emerald-50/50 mx-auto mb-3" />
                  <p className="text-emerald-50/70 mb-4">
                    Unable to load market data. Please check your connection.
                  </p>
                  <button
                    onClick={() => window.location.reload()}
                    className="px-4 py-2 bg-white text-green-950 text-sm font-semibold rounded-full hover:bg-neutral-100 transition-colors"
                  >
                    Retry
                  </button>
                </div>
              ) : marketRows.length === 0 ? (
                <div className="bg-green-950/30 border border-emerald-700/40 rounded-2xl p-6 text-center">
                  <p className="text-emerald-50/70 mb-4">
                    No market data available yet.
                  </p>
                  <button
                    onClick={handleContributeData}
                    className="px-4 py-2 bg-white text-green-950 text-sm font-semibold rounded-full hover:bg-neutral-100 transition-colors"
                  >
                    Contribute the first price
                  </button>
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {marketRows.map((row) => (
                    <MarketCard
                      key={row.cropKey || row.crop}
                      row={row}
                    />
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* ─── Infrastructure Challenge Section ─── */}
          <section>
            <div className="text-center mb-16">
              <h2 className="text-4xl lg:text-5xl font-black mb-6 text-white">
                The <span className="text-emerald-100">Infrastructure</span>{" "}
                Challenge
              </h2>
              <p className="text-xl text-emerald-50/80 max-w-4xl mx-auto leading-relaxed">
                Building the fundamental infrastructure for planetary-scale food
                resilience and environmental continuity.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[
                {
                  title: "Technology must serve the table",
                  description:
                    "Technology must serve the table. Orrith turns data into market insights that help farmers sell efficiently and avoid waste.",
                },
                {
                  title: "Access is a coordination problem",
                  description:
                    "Orrith connects surplus supply with demand through transparent discovery and trusted trading.",
                },
                {
                  title: "Producers need intelligence, not dependency",
                  description:
                    "Direct access to buyers, predictive insights, and climate alerts help farmers sell fairly.",
                },
                {
                  title: "Ecological limits require smarter systems",
                  description:
                    "Every wasted crop is wasted water and soil. Orrith aligns operations with regenerative outcomes.",
                },
                {
                  title: "Infrastructure must outlast individuals",
                  description:
                    "We build modular systems designed to evolve over decades without breaking local realities.",
                },
                {
                  title: "Sensors, data, and humans become one network",
                  description:
                    "Sensors, phones, and intelligence form one living network that moves food in hours, not weeks.",
                },
              ].map((feature) => (
                <div
                  key={feature.title}
                  className="bg-green-950/30 border border-emerald-700/40 rounded-2xl p-6 hover:border-emerald-700/60 transition-colors"
                >
                  <h3 className="text-lg font-bold mb-3 text-white">
                    {feature.title}
                  </h3>
                  <p className="text-sm text-emerald-50/80 leading-relaxed">
                    {feature.description}
                  </p>
                </div>
              ))}
            </div>
          </section>

          {/* ─── Orrith Stack Section ─── */}
          <section>
            <div className="text-center mb-16">
              <h2 className="text-4xl lg:text-5xl font-black mb-6 text-white">
                The <span className="text-emerald-100">Orrith Stack</span>
              </h2>
              <p className="text-xl text-emerald-50/80 max-w-4xl mx-auto leading-relaxed">
                A planetary intelligence architecture connecting environmental
                signals, agricultural operations, markets, logistics, and human
                impact into one adaptive system.
              </p>
            </div>

            <div className="relative max-w-5xl mx-auto">
              <div className="absolute left-1/2 top-0 bottom-0 w-px bg-emerald-700/30 hidden md:block" />
              {[
                {
                  title: "Environmental Intelligence Layer",
                  description:
                    "Climate signals, soil intelligence, water systems, and ecological conditions powering decisions before risks become failures.",
                },
                {
                  title: "Agricultural Network Layer",
                  description:
                    "Farms, sensors, field operations, and production data connected into a real-time agricultural operating network.",
                },
                {
                  title: "Market Intelligence Layer",
                  description:
                    "Price signals, demand forecasting, supply visibility, and predictive analytics helping producers and buyers act with confidence.",
                },
                {
                  title: "Logistics Coordination Layer",
                  description:
                    "Routing, storage coordination, redistribution systems, and movement intelligence reducing waste across the food chain.",
                },
                {
                  title: "Human Impact Layer",
                  description:
                    "Farmers, communities, institutions, and organizations connected through transparent systems designed for shared resilience.",
                },
              ].map((layer, index) => (
                <div
                  key={layer.title}
                  className={`relative flex ${index % 2 === 0 ? "md:justify-start" : "md:justify-end"} mb-8`}
                >
                    <div className="w-full md:w-[46%] bg-green-950/30 border border-emerald-700/40 rounded-2xl p-6 hover:border-emerald-700/60 transition-colors">
                      <h3 className="font-bold text-lg text-white text-center mb-3">
                        {layer.title}
                      </h3>
                      <p className="text-sm text-emerald-50/80 leading-relaxed">
                        {layer.description}
                      </p>
                    </div>
                </div>
              ))}
            </div>
          </section>

          {/* ─── Final CTA Section ─── */}
          <section>
            <div className="max-w-4xl mx-auto px-8 text-center">
              <h2 className="text-4xl lg:text-5xl font-black mb-6 text-white">
                Ready to Transform Food Operations?
              </h2>
              <p className="text-xl text-emerald-100 mb-8 leading-relaxed">
                Join emerging network building the intelligence layer for global
                food resilience.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
                <button
                  onClick={handleStartPilot}
                  className="px-10 py-4 bg-emerald-300 hover:bg-emerald-200 text-green-950 rounded-xl font-bold text-lg shadow-lg transition-colors focus:outline-none focus:ring-2 focus:ring-emerald-200"
                >
                  <span className="flex items-center gap-2">
                    <Zap className="w-5 h-5" />
                    Build With Orrith
                  </span>
                </button>
              </div>
            </div>
          </section>
        </div>
      </main>
    </div>
  );
}
